import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useTenant } from '@/core/hooks/useTenant';
import { useAuth } from '@/core/AuthContext';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import {
  uploadAttachment,
  fetchAttachmentsByResponseId,
  deleteAttachment,
} from '../api/responseAttachments';

export function useResponseAttachments(responseId: string | null | undefined) {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaResponseAttachments(responseId ?? null, selectedTenantId),
    queryFn: () => fetchAttachmentsByResponseId(responseId!, selectedTenantId ?? undefined),
    enabled: !!responseId && !!selectedTenantId,
  });
}

export function useUploadResponseAttachment() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { profile } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ responseId, file }: { responseId: string; file: File; completionId?: string }) =>
      uploadAttachment(responseId, file, {
        tenantId: selectedTenantId ?? undefined,
        createdBy: profile?.id ?? null,
      }),
    onSuccess: (attachment, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaResponseAttachments(attachment.response_id, selectedTenantId),
      });
      if (variables.completionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.modLpaCompletionDetail(variables.completionId, selectedTenantId),
        });
      }
      toast({
        title: t('mod_lpa.attachments.uploadSuccess', 'Attachment uploaded'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.attachments.uploadError', 'Failed to upload attachment'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteResponseAttachment() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      attachmentId,
    }: {
      attachmentId: string;
      responseId: string;
      completionId?: string;
    }) =>
      deleteAttachment(attachmentId, selectedTenantId ?? undefined),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaResponseAttachments(variables.responseId, selectedTenantId),
      });
      if (variables.completionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.modLpaCompletionDetail(variables.completionId, selectedTenantId),
        });
      }
      toast({
        title: t('mod_lpa.attachments.deleteSuccess', 'Attachment deleted'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.attachments.deleteError', 'Failed to delete attachment'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
